import { parseHotkey, compareHotkey } from 'is-hotkey';
import isApplePlatform from '@atlas/tool/box/browser/isApplePlatform';
import lastCapturedKeyDowns from '@atlas/tool/box/event/lastCapturedKeyDowns';
import stringifyHotkey from '@atlas/tool/box/event/stringifyHotkey';

/**
 * @param input the hotkey should be set in specific order: ctrl+shift+alt+cmd+KEY (drop excess modifiers)
 * @param _category The group to be used in help modal
 * @param _description Explanation to be used in help modal
 * @param _sort Set position in the help modal
 */
function _hotkey(input: THotkeyInput, _category: THotkeyHiddenCategory): THotkey;
function _hotkey(input: THotkeyInput, _category: THotkeyCategory, _description: string, _sort?: number): THotkey;
function _hotkey(input: THotkeyInput, _category: string, _description?: string, _sort?: number): THotkey {
    if (typeof input === 'string' || Array.isArray(input)) {
        const hotkeyInstance = parseHotkeyInput(input);
        return {
            mac: hotkeyInstance,
            win: hotkeyInstance,
            length: hotkeyInstance.length,
            matches,
            toString,
        };
    }

    return {
        mac: input.mac && parseHotkeyInput<THotkeyMac>(input.mac),
        win: input.win && parseHotkeyInput<THotkeyWin>(input.win),
        length: (isApplePlatform() ? input.mac?.length : input.win?.length) ?? 0,
        matches,
        toString,
    };
}

const hotkey = Object.assign(_hotkey, {
    matches: (hotkey: THotkeyData, eventData: THotkeyMatchingData) => matches.call(hotkey, eventData),
    stringify: (hotkey: THotkeyData) => toString.call(hotkey),
});

export default hotkey;

// #region Helpers

export const hotkeyCategories = [
    'General',
    'Navigation',
    'Inbox',
    'Ticket actions',
    'Composer',
    'Session recording',
] as const;

const sequenceWaitTime = 1e3; // 1 second

// TODO: Would be nice to determine the result type depending on the input
// but it is not possible because Any and Win has the same structure, or I missed something
export function parseHotkeyInput<TResult extends THotkeyAnyOS | THotkeyMac | THotkeyWin = THotkeyAnyOS>(
    hotkey:
        | THotkeyAnyOSString
        | THotkeyMacString
        | THotkeyWinString
        | THotkeySequence<THotkeyAnyOSString | THotkeyMacString | THotkeyWinString>,
): THotkeySequence<TResult> {
    if (!Array.isArray(hotkey)) return [parseHotkey(hotkey) as TResult];
    return hotkey.map((hotkey) => parseHotkey(hotkey) as TResult) as THotkeySequence<TResult>;
}

function compareHotkeyData(hotkeyData: THotkeyAnyOS | THotkeyMac | THotkeyWin, eventData: THotkeyMatchingData) {
    // This type assignment is fine because we are never using `key` comparison from is-hotkey library
    return compareHotkey(hotkeyData, eventData as THotkeyMatchingData & { key: string });
}

function toString(this: THotkeyData, missing = 'no') {
    return stringifyHotkey(this) ?? missing;
}

function matches(this: THotkeyData, eventData: THotkeyMatchingData | { nativeEvent: THotkeyMatchingData }): boolean {
    const platformHotkey = isApplePlatform() ? this.mac : this.win;
    if (!platformHotkey) return false;

    const [enterHotkey, followingHotkey] = platformHotkey;

    if (!followingHotkey) {
        const latestEvent = Array.isArray(eventData) ? eventData[eventData.length - 1] : eventData;
        return compareHotkeyData(enterHotkey, latestEvent);
    }

    const [latestEvent, previousEvent] = lastCapturedKeyDowns;

    if (!latestEvent) return false;

    if (latestEvent !== eventData && (!('nativeEvent' in eventData) || latestEvent !== eventData.nativeEvent)) {
        console.error(
            "Matching of sequential hotkeys can be either complex or magical.\nThis one is magical just as you ✨are✨.\nAnd so you should know that magic has some rules.\nIn this case you should only match the hotkey to the latest, ongoing keydown event.\nIf it doesn't work for you, ping @Stan 🦄",
        );
        return false;
    }

    if (!previousEvent) return false;

    if (latestEvent.timeStamp - previousEvent.timeStamp > sequenceWaitTime) return false;

    return compareHotkeyData(enterHotkey, previousEvent) && compareHotkeyData(followingHotkey, latestEvent);
}

const mapKeyToCode = {
    // Alphabetical
    a: 65,
    b: 66,
    c: 67,
    d: 68,
    e: 69,
    f: 70,
    g: 71,
    h: 72,
    i: 73,
    j: 74,
    k: 75,
    l: 76,
    m: 77,
    n: 78,
    o: 79,
    p: 80,
    q: 81,
    r: 82,
    s: 83,
    t: 84,
    u: 85,
    v: 86,
    w: 87,
    x: 88,
    y: 89,
    z: 90,

    // Numeric
    0: 48,
    1: 49,
    2: 50,
    3: 51,
    4: 52,
    5: 53,
    6: 54,
    7: 55,
    8: 56,
    9: 57,

    // Arrow
    down: 40,
    left: 37,
    right: 39,
    up: 38,

    // Special
    '?': 63,
    '[': 219,
    ']': 221,
    '/': 191,
    '-': 189,
    '.': 190,
    ',': 188,
    backspace: 8,
    delete: 46,
    enter: 13,
    escape: 27,
    space: 32,
    tab: 9,
} as const;

// #endregion

// #region Types

type THotkeyHiddenCategory = 'hidden';

export type THotkeyCategory = typeof hotkeyCategories[number];

type THotkeyMatchingData = Pick<KeyboardEvent, 'which' | 'altKey' | 'ctrlKey' | 'shiftKey' | 'metaKey'>;

export type THotkeyAnyOS = {
    which: THotkeyCode;
    ctrlKey: boolean;
    shiftKey: boolean;
    altKey: boolean;
    metaKey: false;
};

export type THotkeyMac = Omit<THotkeyAnyOS, 'metaKey'> & { metaKey: boolean };

export type THotkeyWin = Omit<THotkeyAnyOS, 'metaKey'> & { metaKey: false };

// Used for input, output, and event data
export type THotkeySequence<
    T extends
        | THotkeyAnyOS
        | THotkeyMac
        | THotkeyWin
        | THotkeyAnyOSString
        | THotkeyMacString
        | THotkeyWinString
        | THotkeyMatchingData,
> = [T] | [T, T];

export type THotkeyData = {
    mac?: THotkeySequence<THotkeyMac>;
    win?: THotkeySequence<THotkeyWin>;
};

export type THotkey = THotkeyData & {
    matches: typeof matches;
    toString: typeof toString;
    length: number;
};

// ❗ If you are adding any keys here, please update THotkeyMapToCode and tests to ensure that key code is correct
// ❗ Also update stringifyHotkey.tsx to ensure that the key is displayed correctly
type THotkeySpecialKey =
    | '?'
    | '['
    | ']'
    | '/'
    | '-'
    | 'backspace'
    | 'delete'
    | 'enter'
    | 'escape'
    | 'space'
    | 'tab'
    | '.'
    | ',';
type THotkeyArrowKey = 'down' | 'left' | 'right' | 'up';
type THotkeyNumericKey = '0' | '1' | '2' | '3' | '4' | '5' | '6' | '7' | '8' | '9';
type THotkeyAlphabeticalKey =
    | 'a'
    | 'b'
    | 'c'
    | 'd'
    | 'e'
    | 'f'
    | 'g'
    | 'h'
    | 'i'
    | 'j'
    | 'k'
    | 'l'
    | 'm'
    | 'n'
    | 'o'
    | 'p'
    | 'q'
    | 'r'
    | 's'
    | 't'
    | 'u'
    | 'v'
    | 'w'
    | 'x'
    | 'y'
    | 'z';

export type THotkeyKey = THotkeyAlphabeticalKey | THotkeyNumericKey | THotkeyArrowKey | THotkeySpecialKey;

type THotkeyMapToCode = { [K in THotkeyKey]: number };

export type THotkeyCode = typeof mapKeyToCode[THotkeyKey];

// 'win' key is not available as it is not common to use it in Windows shortcuts
export type THotkeyWinString = `${'ctrl+' | ''}${'shift+' | ''}${'alt+' | ''}${THotkeyKey}`;

export type THotkeyMacString = `${'ctrl+' | ''}${'shift+' | ''}${'alt+' | ''}${'cmd+' | ''}${THotkeyKey}`;

type THotkeyAnyOSString = THotkeyMacString & THotkeyWinString;

export type THotkeyInput =
    | THotkeyAnyOSString
    | THotkeySequence<THotkeyAnyOSString>
    | {
          mac?: THotkeyMacString | THotkeySequence<THotkeyMacString>;
          win?: THotkeyWinString | THotkeySequence<THotkeyWinString>;
      };

// #endregion

// #region Tests

if (import.meta.vitest) {
    const { describe, it, vi, expect, expectTypeOf, beforeAll, afterEach, afterAll } = import.meta.vitest;

    const altWith = (key: THotkeyKey): THotkeySequence<THotkeyAnyOS> => [
        {
            which: mapKeyToCode[key],
            altKey: true,
            ctrlKey: false,
            shiftKey: false,
            metaKey: false,
        },
    ];

    const hk = (input: THotkeyInput) => hotkey(input, 'hidden');

    describe('hotkey', () => {
        beforeAll(() => {
            vi.mock('@atlas/tool/box/event/stringifyHotkey', async () => {
                const module = await vi.importActual<typeof import('@atlas/tool/box/event/stringifyHotkey')>(
                    '@atlas/tool/box/event/stringifyHotkey',
                );
                return { default: vi.fn(module.default) };
            });
        });

        beforeAll(() => {
            vi.mock('@atlas/tool/box/browser/isApplePlatform', () => {
                let _flag = false;
                return {
                    default: Object.assign(() => _flag, {
                        set(flag: boolean) {
                            _flag = flag;
                        },
                    }),
                };
            });
        });

        afterEach(() => {
            vi.mocked(stringifyHotkey).mockClear();
            switchToWindows();
        });

        afterAll(() => {
            vi.resetAllMocks();
        });

        function switchToApple() {
            (isApplePlatform as unknown as { set: (flag: boolean) => void }).set(true);
        }

        function switchToWindows() {
            (isApplePlatform as unknown as { set: (flag: boolean) => void }).set(false);
        }

        describe('types', () => {
            it('has all the keys listed in the map', () => {
                // If this test fails you probably missed some key in the map
                expectTypeOf(mapKeyToCode).toMatchTypeOf<THotkeyMapToCode>();
            });

            it('supports various string inputs', () => {
                expectTypeOf('a' as const).toMatchTypeOf<THotkeyInput>();
                expectTypeOf('ctrl+b' as const).toMatchTypeOf<THotkeyInput>();
                expectTypeOf('shift+c' as const).toMatchTypeOf<THotkeyInput>();
                expectTypeOf('alt+g' as const).toMatchTypeOf<THotkeyInput>();
                expectTypeOf('ctrl+shift+i' as const).toMatchTypeOf<THotkeyInput>();
                expectTypeOf('ctrl+alt+m' as const).toMatchTypeOf<THotkeyInput>();
                expectTypeOf('shift+alt+n' as const).toMatchTypeOf<THotkeyInput>();
                expectTypeOf('ctrl+shift+alt+l' as const).toMatchTypeOf<THotkeyInput>();
                expectTypeOf('ctrl+space' as const).toMatchTypeOf<THotkeyInput>();
                expectTypeOf('shift+up' as const).toMatchTypeOf<THotkeyInput>();
                expectTypeOf('down' as const).toMatchTypeOf<THotkeyInput>();
                expectTypeOf('alt+enter' as const).toMatchTypeOf<THotkeyInput>();
                expectTypeOf('ctrl+shift+?' as const).toMatchTypeOf<THotkeyInput>();
                expectTypeOf('alt+[' as const).toMatchTypeOf<THotkeyInput>();
            });

            it('supports various platform specific inputs', () => {
                expectTypeOf({ mac: 'cmd+o' } as const).toMatchTypeOf<THotkeyInput>();
                expectTypeOf({ win: 'ctrl+shift+p' } as const).toMatchTypeOf<THotkeyInput>();
                expectTypeOf({ mac: 'cmd+r', win: 'ctrl+shift+s' } as const).toMatchTypeOf<THotkeyInput>();
            });
        });

        it('generates all possible shared hotkey with proper key codes', () => {
            const { mac, win } = hk('ctrl+shift+alt+a');
            expect(mac).toEqual(win);

            expect(mac).toEqual([
                {
                    which: mapKeyToCode.a,
                    altKey: true,
                    ctrlKey: true,
                    shiftKey: true,
                    metaKey: false,
                },
            ]);

            expect(hk('alt+b').mac).toEqual(altWith('b'));
            expect(hk('alt+c').mac).toEqual(altWith('c'));
            expect(hk('alt+d').mac).toEqual(altWith('d'));
            expect(hk('alt+e').mac).toEqual(altWith('e'));
            expect(hk('alt+f').mac).toEqual(altWith('f'));
            expect(hk('alt+g').mac).toEqual(altWith('g'));
            expect(hk('alt+h').mac).toEqual(altWith('h'));
            expect(hk('alt+i').mac).toEqual(altWith('i'));
            expect(hk('alt+j').mac).toEqual(altWith('j'));
            expect(hk('alt+k').mac).toEqual(altWith('k'));
            expect(hk('alt+l').mac).toEqual(altWith('l'));
            expect(hk('alt+m').mac).toEqual(altWith('m'));
            expect(hk('alt+n').mac).toEqual(altWith('n'));
            expect(hk('alt+o').mac).toEqual(altWith('o'));
            expect(hk('alt+p').mac).toEqual(altWith('p'));
            expect(hk('alt+q').mac).toEqual(altWith('q'));
            expect(hk('alt+r').mac).toEqual(altWith('r'));
            expect(hk('alt+s').mac).toEqual(altWith('s'));
            expect(hk('alt+t').mac).toEqual(altWith('t'));
            expect(hk('alt+u').mac).toEqual(altWith('u'));
            expect(hk('alt+v').mac).toEqual(altWith('v'));
            expect(hk('alt+w').mac).toEqual(altWith('w'));
            expect(hk('alt+x').mac).toEqual(altWith('x'));
            expect(hk('alt+y').mac).toEqual(altWith('y'));
            expect(hk('alt+z').mac).toEqual(altWith('z'));

            expect(hk('alt+0').mac).toEqual(altWith('0'));
            expect(hk('alt+1').mac).toEqual(altWith('1'));
            expect(hk('alt+2').mac).toEqual(altWith('2'));
            expect(hk('alt+3').mac).toEqual(altWith('3'));
            expect(hk('alt+4').mac).toEqual(altWith('4'));
            expect(hk('alt+5').mac).toEqual(altWith('5'));
            expect(hk('alt+6').mac).toEqual(altWith('6'));
            expect(hk('alt+7').mac).toEqual(altWith('7'));
            expect(hk('alt+8').mac).toEqual(altWith('8'));
            expect(hk('alt+9').mac).toEqual(altWith('9'));

            expect(hk('alt+down').mac).toEqual(altWith('down'));
            expect(hk('alt+left').mac).toEqual(altWith('left'));
            expect(hk('alt+right').mac).toEqual(altWith('right'));
            expect(hk('alt+up').mac).toEqual(altWith('up'));

            expect(hk('alt+?').mac).toEqual(altWith('?'));
            expect(hk('alt+[').mac).toEqual(altWith('['));
            expect(hk('alt+]').mac).toEqual(altWith(']'));
            expect(hk('alt+/').mac).toEqual(altWith('/'));
            expect(hk('alt+backspace').mac).toEqual(altWith('backspace'));
            expect(hk('alt+delete').mac).toEqual(altWith('delete'));
            expect(hk('alt+enter').mac).toEqual(altWith('enter'));
            expect(hk('alt+escape').mac).toEqual(altWith('escape'));
            expect(hk('alt+space').mac).toEqual(altWith('space'));
            expect(hk('alt+tab').mac).toEqual(altWith('tab'));
            expect(hk('alt+.').mac).toEqual(altWith('.'));
            expect(hk('alt+,').mac).toEqual(altWith(','));
        });

        it('generates platform specific hotkeys', () => {
            const { mac, win } = hk({ mac: 'cmd+o', win: 'ctrl+shift+p' });
            expect(mac).not.toEqual(win);

            expect(mac).toEqual([
                {
                    which: mapKeyToCode.o,
                    altKey: false,
                    ctrlKey: false,
                    shiftKey: false,
                    metaKey: true,
                },
            ]);

            expect(win).toEqual([
                {
                    which: mapKeyToCode.p,
                    altKey: false,
                    ctrlKey: true,
                    shiftKey: true,
                    metaKey: false,
                },
            ]);

            expect(hk({ mac: 'cmd+r' })).to.have.property('win', undefined);
            expect(hk({ mac: 'cmd+r' }).mac).toEqual([
                {
                    which: mapKeyToCode.r,
                    altKey: false,
                    ctrlKey: false,
                    shiftKey: false,
                    metaKey: true,
                },
            ]);

            expect(hk({ win: 'ctrl+r' })).to.have.property('mac', undefined);
            expect(hk({ win: 'ctrl+r' }).win).toEqual([
                {
                    which: mapKeyToCode.r,
                    altKey: false,
                    ctrlKey: true,
                    shiftKey: false,
                    metaKey: false,
                },
            ]);
        });

        it('can stringify the result', () => {
            expect(hk('ctrl+shift+alt+a').toString()).toBe('Ctrl+Shift+Alt+A');

            expect(stringifyHotkey).toBeCalledWith(
                expect.objectContaining({
                    mac: [{ which: mapKeyToCode.a, altKey: true, ctrlKey: true, shiftKey: true, metaKey: false }],
                    win: [{ which: mapKeyToCode.a, altKey: true, ctrlKey: true, shiftKey: true, metaKey: false }],
                }),
            );

            expect(`${hk('alt+?')}`).toBe('Alt+?');
            expect(stringifyHotkey).toBeCalledWith(
                expect.objectContaining({
                    mac: [{ which: mapKeyToCode['?'], altKey: true, ctrlKey: false, shiftKey: false, metaKey: false }],
                    win: [{ which: mapKeyToCode['?'], altKey: true, ctrlKey: false, shiftKey: false, metaKey: false }],
                }),
            );
        });

        it('can compare the result with the event', () => {
            const saveHotkey = hk('ctrl+s');

            const eventData = {
                which: mapKeyToCode.s,
                ctrlKey: true,
                shiftKey: false,
                altKey: false,
                metaKey: false,
            };

            const event = new KeyboardEvent('keydown', eventData);
            expect(saveHotkey.matches(event)).toBe(true);

            const eventWithNotMatchingCode = new KeyboardEvent('keydown', {
                ...eventData,
                which: 10,
            });
            expect(saveHotkey.matches(eventWithNotMatchingCode)).toBe(false);

            const eventWithNotMatchingCtrl = new KeyboardEvent('keydown', {
                ...eventData,
                ctrlKey: !eventData.ctrlKey,
            });
            expect(saveHotkey.matches(eventWithNotMatchingCtrl)).toBe(false);

            const eventWithNotMatchingShift = new KeyboardEvent('keydown', {
                ...eventData,
                shiftKey: !eventData.shiftKey,
            });
            expect(saveHotkey.matches(eventWithNotMatchingShift)).toBe(false);

            const eventWithNotMatchingAlt = new KeyboardEvent('keydown', {
                ...eventData,
                altKey: !eventData.altKey,
            });
            expect(saveHotkey.matches(eventWithNotMatchingAlt)).toBe(false);

            const eventWithNotMatchingMeta = new KeyboardEvent('keydown', {
                ...eventData,
                metaKey: !eventData.metaKey,
            });
            expect(saveHotkey.matches(eventWithNotMatchingMeta)).toBe(false);
        });

        it('compares platform specific hotkeys', () => {
            const saveHotkey = hk({ mac: 'cmd+s', win: 'ctrl+s' });
            const cmdEvent = new KeyboardEvent('keydown', { which: mapKeyToCode.s, metaKey: true });
            const ctrlEvent = new KeyboardEvent('keydown', { which: mapKeyToCode.s, ctrlKey: true });

            switchToWindows();
            expect(saveHotkey.matches(cmdEvent)).toBe(false);
            expect(saveHotkey.matches(ctrlEvent)).toBe(true);

            switchToApple();
            expect(saveHotkey.matches(cmdEvent)).toBe(true);
            expect(saveHotkey.matches(ctrlEvent)).toBe(false);
        });

        it('stringifies given hotkey', () => {
            const randomHotkey = hk('ctrl+shift+alt+z');
            expect(hotkey.stringify(randomHotkey)).toBe('Ctrl+Shift+Alt+Z');
            expect(stringifyHotkey).toBeCalledWith(
                expect.objectContaining({
                    mac: [{ which: mapKeyToCode.z, altKey: true, ctrlKey: true, shiftKey: true, metaKey: false }],
                    win: [{ which: mapKeyToCode.z, altKey: true, ctrlKey: true, shiftKey: true, metaKey: false }],
                }),
            );
        });

        it('compares stored hotkey with event', () => {
            const randomHotkey = hk('ctrl+shift+alt+z');

            expect(
                hotkey.matches(
                    randomHotkey,
                    new KeyboardEvent('keydown', {
                        which: mapKeyToCode.z,
                        altKey: true,
                        ctrlKey: true,
                        shiftKey: true,
                        metaKey: false,
                    }),
                ),
            ).toBe(true);

            expect(
                hotkey.matches(
                    randomHotkey,
                    new KeyboardEvent('keydown', {
                        which: mapKeyToCode.down,
                        altKey: true,
                        ctrlKey: true,
                        shiftKey: true,
                        metaKey: false,
                    }),
                ),
            ).toBe(false);

            expect(
                hotkey.matches(
                    randomHotkey,
                    new KeyboardEvent('keydown', {
                        which: mapKeyToCode.z,
                        altKey: !true,
                        ctrlKey: true,
                        shiftKey: true,
                        metaKey: false,
                    }),
                ),
            ).toBe(false);

            expect(
                hotkey.matches(
                    randomHotkey,
                    new KeyboardEvent('keydown', {
                        which: mapKeyToCode.z,
                        altKey: true,
                        ctrlKey: !true,
                        shiftKey: true,
                        metaKey: false,
                    }),
                ),
            ).toBe(false);

            expect(
                hotkey.matches(
                    randomHotkey,
                    new KeyboardEvent('keydown', {
                        which: mapKeyToCode.z,
                        altKey: true,
                        ctrlKey: true,
                        shiftKey: !true,
                        metaKey: false,
                    }),
                ),
            ).toBe(false);

            expect(
                hotkey.matches(
                    randomHotkey,
                    new KeyboardEvent('keydown', {
                        which: mapKeyToCode.z,
                        altKey: true,
                        ctrlKey: true,
                        shiftKey: true,
                        metaKey: !false,
                    }),
                ),
            ).toBe(false);
        });

        describe(':sequence', () => {
            beforeAll(() => {
                vi.spyOn(console, 'error');
            });

            afterEach(() => {
                lastCapturedKeyDowns[0] = undefined;
                lastCapturedKeyDowns[1] = undefined;
            });

            function setLastEvents(latest: KeyboardEvent | undefined, previous: KeyboardEvent | undefined) {
                lastCapturedKeyDowns[0] = latest;
                lastCapturedKeyDowns[1] = previous;
            }

            it('generates shared hotkey', () => {
                const { mac, win } = hk(['ctrl+shift+alt+up', 'shift+?']);
                expect(mac).toEqual(win);

                expect(mac).toEqual([
                    {
                        which: mapKeyToCode.up,
                        altKey: true,
                        ctrlKey: true,
                        shiftKey: true,
                        metaKey: false,
                    },
                    {
                        which: mapKeyToCode['?'],
                        altKey: false,
                        ctrlKey: false,
                        shiftKey: true,
                        metaKey: false,
                    },
                ]);
            });

            it('generates platform specific hotkeys', () => {
                const { mac, win } = hk({ mac: ['cmd+space', '/'], win: ['ctrl+enter', 'backspace'] });
                expect(mac).not.toEqual(win);

                expect(mac).toEqual([
                    {
                        which: mapKeyToCode.space,
                        altKey: false,
                        ctrlKey: false,
                        shiftKey: false,
                        metaKey: true,
                    },
                    {
                        which: mapKeyToCode['/'],
                        altKey: false,
                        ctrlKey: false,
                        shiftKey: false,
                        metaKey: false,
                    },
                ]);

                expect(win).toEqual([
                    {
                        which: mapKeyToCode.enter,
                        altKey: false,
                        ctrlKey: true,
                        shiftKey: false,
                        metaKey: false,
                    },
                    {
                        which: mapKeyToCode.backspace,
                        altKey: false,
                        ctrlKey: false,
                        shiftKey: false,
                        metaKey: false,
                    },
                ]);

                expect(hk({ mac: ['cmd+delete', 'right'] })).to.have.property('win', undefined);
                expect(hk({ mac: ['cmd+delete', '0'] }).mac).toEqual([
                    {
                        which: mapKeyToCode.delete,
                        altKey: false,
                        ctrlKey: false,
                        shiftKey: false,
                        metaKey: true,
                    },
                    {
                        which: mapKeyToCode['0'],
                        altKey: false,
                        ctrlKey: false,
                        shiftKey: false,
                        metaKey: false,
                    },
                ]);

                expect(hk({ win: ['ctrl+[', 'shift+]'] })).to.have.property('mac', undefined);
                expect(hk({ win: ['ctrl+[', 'shift+]'] }).win).toEqual([
                    {
                        which: mapKeyToCode['['],
                        altKey: false,
                        ctrlKey: true,
                        shiftKey: false,
                        metaKey: false,
                    },
                    {
                        which: mapKeyToCode[']'],
                        altKey: false,
                        ctrlKey: false,
                        shiftKey: true,
                        metaKey: false,
                    },
                ]);
            });

            it('can stringify the result', () => {
                expect(hk(['ctrl+shift+alt+a', 'shift+?']).toString()).toBe('Ctrl+Shift+Alt+A Shift+?');

                expect(stringifyHotkey).toBeCalledWith(
                    expect.objectContaining({
                        mac: [
                            { which: mapKeyToCode.a, altKey: true, ctrlKey: true, shiftKey: true, metaKey: false },
                            { which: mapKeyToCode['?'], altKey: false, ctrlKey: false, shiftKey: true, metaKey: false },
                        ],
                        win: [
                            { which: mapKeyToCode.a, altKey: true, ctrlKey: true, shiftKey: true, metaKey: false },
                            { which: mapKeyToCode['?'], altKey: false, ctrlKey: false, shiftKey: true, metaKey: false },
                        ],
                    }),
                );

                expect(`${hk(['alt+?', 'g'])}`).toBe('Alt+? G');
                expect(stringifyHotkey).toBeCalledWith(
                    expect.objectContaining({
                        mac: [
                            { which: mapKeyToCode['?'], altKey: true, ctrlKey: false, shiftKey: false, metaKey: false },
                            { which: mapKeyToCode.g, altKey: false, ctrlKey: false, shiftKey: false, metaKey: false },
                        ],
                        win: [
                            { which: mapKeyToCode['?'], altKey: true, ctrlKey: false, shiftKey: false, metaKey: false },
                            { which: mapKeyToCode.g, altKey: false, ctrlKey: false, shiftKey: false, metaKey: false },
                        ],
                    }),
                );
            });

            it('stringifies given hotkey', () => {
                const randomHotkey = hk(['g', 'i']);
                expect(hotkey.stringify(randomHotkey)).toBe('G I');
                expect(stringifyHotkey).toBeCalledWith(
                    expect.objectContaining({
                        mac: [
                            { which: mapKeyToCode.g, altKey: false, ctrlKey: false, shiftKey: false, metaKey: false },
                            { which: mapKeyToCode.i, altKey: false, ctrlKey: false, shiftKey: false, metaKey: false },
                        ],
                        win: [
                            { which: mapKeyToCode.g, altKey: false, ctrlKey: false, shiftKey: false, metaKey: false },
                            { which: mapKeyToCode.i, altKey: false, ctrlKey: false, shiftKey: false, metaKey: false },
                        ],
                    }),
                );
            });

            it('prints error to the console if compared not to the latest event', () => {
                const latestEvent = new KeyboardEvent('keydown');
                const previousEvent = new KeyboardEvent('keydown');
                const randomEvent = new KeyboardEvent('keydown');

                setLastEvents(latestEvent, previousEvent);

                expect(console.error).not.toBeCalled();
                hk(['g', 'i']).matches(latestEvent);
                expect(console.error).not.toBeCalled();

                hk(['g', 'i']).matches(previousEvent);
                expect(console.error).toBeCalledTimes(1);

                hk(['g', 'i']).matches(randomEvent);
                expect(console.error).toBeCalledTimes(2);

                hk(['g', 'i']).matches(latestEvent);
                expect(console.error).toBeCalledTimes(2);
            });

            it('results to true if latest events are matching the hotkey, and false otherwise', () => {
                const latestEvent = new KeyboardEvent('keydown', { which: mapKeyToCode.i });
                const previousEvent = new KeyboardEvent('keydown', { which: mapKeyToCode.g });
                setLastEvents(latestEvent, previousEvent);

                expect(hk(['i', 'g']).matches(latestEvent)).toBe(false);

                expect(hk(['g', 'i']).matches(latestEvent)).toBe(true);

                expect(hk(['ctrl+g', 'i']).matches(latestEvent)).toBe(false);

                expect(hk(['g', 'alt+i']).matches(latestEvent)).toBe(false);
            });

            it(`is only results to true if the time difference between events is less than ${sequenceWaitTime}ms`, () => {
                const latestEventTime = 1e9;
                const latestEvent = {
                    which: mapKeyToCode.i,
                    ctrlKey: false,
                    shiftKey: false,
                    altKey: false,
                    metaKey: false,
                    timeStamp: latestEventTime,
                } as KeyboardEvent;
                const previousEvent = {
                    which: mapKeyToCode.g,
                    ctrlKey: false,
                    shiftKey: false,
                    altKey: false,
                    metaKey: false,
                    timeStamp: latestEventTime,
                } as KeyboardEvent;

                setLastEvents(latestEvent, previousEvent);
                expect(hk(['g', 'i']).matches(latestEvent)).toBe(true);

                setLastEvents(latestEvent, { ...previousEvent, timeStamp: latestEventTime - sequenceWaitTime });
                expect(hk(['g', 'i']).matches(latestEvent)).toBe(true);

                setLastEvents(latestEvent, { ...previousEvent, timeStamp: latestEventTime - sequenceWaitTime - 1 });
                expect(hk(['g', 'i']).matches(latestEvent)).toBe(false);

                setLastEvents(latestEvent, { ...previousEvent, timeStamp: latestEventTime - sequenceWaitTime * 2 });
                expect(hk(['g', 'i']).matches(latestEvent)).toBe(false);

                setLastEvents(latestEvent, { ...previousEvent, timeStamp: latestEventTime - sequenceWaitTime });
                expect(hk(['g', 'i']).matches(latestEvent)).toBe(true);
            });

            it('would not match event if not enough events', () => {
                const latestEvent = new KeyboardEvent('keydown', { which: mapKeyToCode.i });
                const previousEvent = new KeyboardEvent('keydown', { which: mapKeyToCode.g });
                expect(hk(['g', 'i']).matches(latestEvent)).toBe(false);

                setLastEvents(latestEvent, undefined);
                expect(hk(['g', 'i']).matches(latestEvent)).toBe(false);

                setLastEvents(latestEvent, previousEvent);
                expect(hk(['g', 'i']).matches(latestEvent)).toBe(true);
            });

            it('works with one event in the sequence', () => {
                const latestEvent = new KeyboardEvent('keydown', { which: mapKeyToCode['?'], shiftKey: true });
                const previousEvent = new KeyboardEvent('keydown', { which: mapKeyToCode.g });

                setLastEvents(latestEvent, undefined);
                expect(hk(['shift+?']).matches(latestEvent)).toBe(true);

                setLastEvents(latestEvent, previousEvent);
                expect(hk(['shift+?']).matches(latestEvent)).toBe(true);

                // It doesn't care about the actual event (DOM) events sequence when matching just one hotkey
                setLastEvents(undefined, undefined);
                expect(hk(['shift+?']).matches(latestEvent)).toBe(true);
                setLastEvents(previousEvent, latestEvent);
                expect(hk(['shift+?']).matches(latestEvent)).toBe(true);
            });

            it('compares platform specific hotkeys', () => {
                const latestEvent = new KeyboardEvent('keydown', { which: mapKeyToCode.i });
                const previousMacEvent = new KeyboardEvent('keydown', { which: mapKeyToCode.g, metaKey: true });
                const previousWinEvent = new KeyboardEvent('keydown', { which: mapKeyToCode.g, ctrlKey: true });

                const inboxHotkey = hk({ mac: ['cmd+g', 'i'], win: ['ctrl+g', 'i'] });

                switchToApple();
                setLastEvents(latestEvent, previousMacEvent);
                expect(inboxHotkey.matches(latestEvent)).toBe(true);
                setLastEvents(latestEvent, previousWinEvent);
                expect(inboxHotkey.matches(latestEvent)).toBe(false);

                switchToWindows();
                setLastEvents(latestEvent, previousWinEvent);
                expect(inboxHotkey.matches(latestEvent)).toBe(true);
                setLastEvents(latestEvent, previousMacEvent);
                expect(inboxHotkey.matches(latestEvent)).toBe(false);
            });

            it('compares stored hotkey with the latest events', () => {
                const latestEvent = new KeyboardEvent('keydown', { which: mapKeyToCode.i });
                const previousEvent = new KeyboardEvent('keydown', { which: mapKeyToCode.g });
                setLastEvents(latestEvent, previousEvent);

                expect(hotkey.matches(hk(['i', 'g']), latestEvent)).toBe(false);

                expect(hotkey.matches(hk(['g', 'i']), latestEvent)).toBe(true);

                expect(hotkey.matches(hk(['ctrl+g', 'i']), latestEvent)).toBe(false);

                expect(hotkey.matches(hk(['g', 'alt+i']), latestEvent)).toBe(false);
            });
        });
    });
}

// #endregion
